/**
 * Date and Time utilities for Adiosoft Production Engine
 * Enforces safe calendar month calculation and Europe/Istanbul timezone representation.
 */

/**
 * Safari-safe date parser. Handles space-separated datetime, ISO strings, timestamps, and invalid inputs.
 */
export function parseSafeDate(input: string | number | Date | null | undefined): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number') {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const d = new Date(Number(trimmed));
      return isNaN(d.getTime()) ? null : d;
    }
    // Safari fix: replace space between date and time with 'T'
    const normalized = trimmed.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2})?)/, '$1T$2');
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Safely adds months to a base timestamp, properly handling variable month lengths
 * (e.g. Jan 31 + 1 month => Feb 28/29, Mar 31 + 1 month => Apr 30)
 */
export function addMonthsSafely(baseTimestamp: number, months: number = 1): number {
  const d = new Date(baseTimestamp);
  const originalDay = d.getDate();
  d.setMonth(d.getMonth() + months);
  // Handle edge case where target month has fewer days than original date
  if (d.getDate() !== originalDay) {
    d.setDate(0); // Clamps to last day of target month
  }
  return d.getTime();
}

/**
 * Format timestamp in Turkish Locale with Europe/Istanbul timezone
 */
export function formatDateTR(
  timestamp?: number | string | Date | null,
  options?: { includeTime?: boolean }
): string {
  const date = parseSafeDate(timestamp);
  if (!date) {
    return 'Belirtilmedi';
  }

  try {
    return new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(options?.includeTime
        ? { hour: '2-digit', minute: '2-digit' }
        : {}),
    }).format(date);
  } catch {
    return date.toLocaleDateString('tr-TR');
  }
}

/**
 * Format compact date: DD.MM.YYYY
 */
export function formatCompactDateTR(timestamp?: number | string | Date | null): string {
  const date = parseSafeDate(timestamp);
  if (!date) {
    return '-';
  }

  try {
    return new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return date.toLocaleDateString('tr-TR');
  }
}

/**
 * Calculate remaining days until expiration
 */
export function calculateRemainingDays(targetTimestamp: number, nowTimestamp = Date.now()): number {
  const diffMs = targetTimestamp - nowTimestamp;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}
